import { useState } from 'react'

import { useTagSettingsGetApi, useTagSettingsUpdateApi } from '@/common/api/modules/searchTag'
import { useSiteId } from '@/common/app/site'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

const numberInput =
  'shadow-inset text-size-13 w-28 rounded-lg border border-border-input bg-input px-3 py-2'

const Field = ({
  label,
  hint,
  children
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) => (
  <div className='mb-4'>
    <Text size={13} weight='bold'>
      {label}
    </Text>
    <div className='mt-1'>{children}</div>
    {hint && (
      <div className='mt-1'>
        <Text size={12} color={TextColor.SECONDARY}>
          {hint}
        </Text>
      </div>
    )}
  </div>
)

const SearchTagSettingsPage = () => {
  const siteId = useSiteId()
  const { data, mutate } = useTagSettingsGetApi({ site_id: siteId })
  // 編集中の差分だけを持ち、未編集の項目はサーバー値をそのまま見る。
  // 取得のたびに setState で丸ごと同期すると、保存直後の再取得で
  // 入力中の値が巻き戻る（effect 内 setState の連鎖レンダーも避ける）
  const [edits, setEdits] = useState<Record<string, number>>({})
  const [message, setMessage] = useState('')

  const { trigger, isMutating } = useTagSettingsUpdateApi({
    onSuccess: () => {
      setMessage('保存しました')
      setEdits({})
      mutate()
    },
    onError: async (res) => {
      const body = await res.json().catch(() => ({}))
      setMessage(`保存に失敗しました: ${body.detail ?? res.status}`)
    }
  })

  if (!data) {
    return (
      <Main type={MainLayoutType.MEDIUM}>
        <PageTitle title='タグ仕様設定' description='Tag Trail のパラメータ' />
        <Content>
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        </Content>
      </Main>
    )
  }

  const num = (k: keyof typeof data) => Number(edits[k as string] ?? data[k] ?? 0)
  const setNum = (k: string, v: string) => setEdits({ ...edits, [k]: Number(v) })

  return (
    <Main type={MainLayoutType.MEDIUM}>
      <PageTitle
        title='タグ仕様設定'
        description='タグの母集団の目標帯や、1商品あたりのタグ数を調整します。機能のON/OFFはサイト管理の「Tag Trail」で切り替えます。'
      />
      <Content>
        {message && (
          <div className='bg-surface-emphasis rounded-lg p-3'>
            <Text size={12}>{message}</Text>
          </div>
        )}

        <Area>
          <AreaContent>
            <Heading>母集団</Heading>
            <div className='mt-2'>
              <Field
                label='目標帯（在庫あり件数）'
                hint='タグページとして成立する商品数の範囲。少なすぎるとページが薄く、多すぎると絞り込みの意味が薄れます。'
              >
                <div className='flex items-center gap-2'>
                  <input
                    className={numberInput}
                    type='number'
                    value={num('population_min')}
                    onChange={(e) => setNum('population_min', e.target.value)}
                  />
                  <Text color={TextColor.SECONDARY}>〜</Text>
                  <input
                    className={numberInput}
                    type='number'
                    value={num('population_max')}
                    onChange={(e) => setNum('population_max', e.target.value)}
                  />
                  <Text color={TextColor.SECONDARY}>件</Text>
                </div>
              </Field>

              <Field
                label='1商品あたりのタグ数'
                hint='商品詳細に出せるタグの数。上限を超えた分は関連度の低いものから外れます。'
              >
                <div className='flex items-center gap-2'>
                  <input
                    className={numberInput}
                    type='number'
                    value={num('product_tag_min')}
                    onChange={(e) => setNum('product_tag_min', e.target.value)}
                  />
                  <Text color={TextColor.SECONDARY}>〜</Text>
                  <input
                    className={numberInput}
                    type='number'
                    value={num('product_tag_max')}
                    onChange={(e) => setNum('product_tag_max', e.target.value)}
                  />
                  <Text color={TextColor.SECONDARY}>個</Text>
                </div>
              </Field>
            </div>

            <Heading>最適化の判定（Phase 3c で使用）</Heading>
            <div className='mt-2'>
              <Field
                label='成果を判定する最低表示回数'
                hint='これ未満の表示数では成果を判定しません。'
              >
                <input
                  className={numberInput}
                  type='number'
                  value={num('min_impressions')}
                  onChange={(e) => setNum('min_impressions', e.target.value)}
                />
              </Field>
              <Field
                label='低CTRとみなす比率（%）'
                hint='サイト中央値に対する比率。下回ると文言変更の候補になります。'
              >
                <input
                  className={numberInput}
                  type='number'
                  value={num('low_ctr_ratio')}
                  onChange={(e) => setNum('low_ctr_ratio', e.target.value)}
                />
              </Field>
            </div>

            <div className='mt-4'>
              <Button
                color={ButtonColor.PRIMARY}
                disabled={isMutating}
                onClick={() =>
                  trigger({
                    site_id: siteId,
                    population_min: num('population_min'),
                    population_max: num('population_max'),
                    product_tag_min: num('product_tag_min'),
                    product_tag_max: num('product_tag_max'),
                    min_impressions: num('min_impressions'),
                    low_ctr_ratio: num('low_ctr_ratio')
                  })
                }
              >
                {isMutating ? '保存中...' : '保存'}
              </Button>
            </div>
          </AreaContent>
        </Area>
      </Content>
    </Main>
  )
}

export default SearchTagSettingsPage
